import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTenantCollection } from '../hooks/useTenantData';
import { useClub } from '../contexts/ClubContext';

// Default fallback events
const defaultEvents = [
  {
    id: 1,
    title: "U16 Tier 1 Training",
    type: "training",
    date: "2026-02-09",
    time: "4:30 PM - 6:00 PM",
    location: "Training Ground",
    team: "U16 Tier 1",
    description: "Regular training session focusing on passing drills."
  },
  {
    id: 2,
    title: "U14 vs San Juan FC",
    type: "match",
    date: "2026-02-15",
    time: "10:00 AM",
    location: "Larry Gomes Stadium",
    team: "U14 Tier 1",
    description: "League match. Players arrive by 9:00 AM."
  }
];

function Schedule() {
  const { club } = useClub();
  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedType, setSelectedType] = useState("All");
  
  // Use tenant-scoped data hook
  const { data: tenantEvents, loading } = useTenantCollection('events');
  
  // Use tenant events if available, otherwise fallback to defaults
  // Format time display for events that have endTime
  const events = (tenantEvents.length > 0 ? tenantEvents : defaultEvents).map(event => ({
    ...event,
    time: event.endTime ? `${event.time} - ${event.endTime}` : event.time
  }));

  const monthNames = ["January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"];

  const eventTypes = ["All", "training", "match", "tournament"];

  const getDaysInMonth = (month, year) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (month, year) => new Date(year, month, 1).getDay();

  const prevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const nextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const getEventsForDate = (day) => {
    const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return events.filter(event => event.date === dateStr);
  };

  const getEventTypeColor = (type) => {
    switch(type) {
      case "training": return "#10b981";
      case "match": return "#ef4444";
      case "tournament": return "#f59e0b";
      default: return "#9ca3af";
    }
  };

  const upcomingEvents = events
    .filter(event => {
      if (selectedType !== "All" && event.type !== selectedType) return false;
      return new Date(event.date) >= new Date(today.toDateString());
    })
    .sort((a, b) => new Date(a.date) - new Date(b.date));

  const daysInMonth = getDaysInMonth(currentMonth, currentYear);
  const firstDay = getFirstDayOfMonth(currentMonth, currentYear);
  const calendarDays = [];

  for (let i = 0; i < firstDay; i++) {
    calendarDays.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
  }

  for (let day = 1; day <= daysInMonth; day++) {
    const dayEvents = getEventsForDate(day);
    const isToday = day === today.getDate() && currentMonth === today.getMonth() && currentYear === today.getFullYear();

    calendarDays.push(
      <div key={day} className={`calendar-day ${isToday ? 'today' : ''} ${dayEvents.length > 0 ? 'has-events' : ''}`}>
        <span className="day-number">{day}</span>
        <div className="day-events">
          {dayEvents.map(event => (
            <div 
              key={event.id}
              className="day-event-dot"
              style={{ backgroundColor: getEventTypeColor(event.type) }}
              onClick={() => setSelectedEvent(event)}
            ></div>
          ))}
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="schedule-page">
        <section className="schedule-hero">
          <h1>SCHEDULE & EVENTS</h1>
          <p>Loading events...</p>
        </section>
      </div>
    );
  }

  return (
    <div className="schedule-page">
      <section className="schedule-hero">
        <h1>SCHEDULE & EVENTS</h1>
        <p>View upcoming training sessions, matches, and tournaments</p>
      </section>

      <section className="calendar-section">
        <div className="calendar-container">
          <div className="calendar-header">
            <button className="calendar-nav" onClick={prevMonth}>&#10094;</button>
            <h2>{monthNames[currentMonth]} {currentYear}</h2>
            <button className="calendar-nav" onClick={nextMonth}>&#10095;</button>
          </div>
          <div className="calendar-weekdays">
            <span>Sun</span><span>Mon</span><span>Tue</span><span>Wed</span><span>Thu</span><span>Fri</span><span>Sat</span>
          </div>
          <div className="calendar-grid">{calendarDays}</div>
          <div className="calendar-legend">
            <div className="legend-item"><span className="legend-dot" style={{ backgroundColor: "#10b981" }}></span><span>Training</span></div>
            <div className="legend-item"><span className="legend-dot" style={{ backgroundColor: "#ef4444" }}></span><span>Match</span></div>
            <div className="legend-item"><span className="legend-dot" style={{ backgroundColor: "#f59e0b" }}></span><span>Tournament</span></div>
          </div>
        </div>
      </section>

      <section className="events-filter">
        <h2>UPCOMING EVENTS</h2>
        <div className="filter-buttons">
          {eventTypes.map((type) => (
            <button key={type} className={`filter-btn ${selectedType === type ? 'active' : ''}`} onClick={() => setSelectedType(type)}>
              {type === "All" ? "All" : type.charAt(0).toUpperCase() + type.slice(1)}
            </button>
          ))}
        </div>
      </section>

      <section className="events-list-section">
        <div className="events-list">
          {upcomingEvents.map(event => (
            <div key={event.id} className="event-card" onClick={() => setSelectedEvent(event)}>
              <div className="event-type-bar" style={{ backgroundColor: getEventTypeColor(event.type) }}></div>
              <div className="event-date-box">
                <span className="event-day">{new Date(event.date).getDate()}</span>
                <span className="event-month">{monthNames[new Date(event.date).getMonth()].slice(0, 3)}</span>
              </div>
              <div className="event-details">
                <h3 className="event-title">{event.title}</h3>
                <div className="event-meta">
                  <span className="event-time">{event.time}</span>
                  <span className="event-location">{event.location}</span>
                </div>
                <span className="event-team">{event.team}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {selectedEvent && (
        <div className="event-modal-overlay" onClick={() => setSelectedEvent(null)}>
          <div className="event-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSelectedEvent(null)}>×</button>
            <div className="modal-header" style={{ backgroundColor: getEventTypeColor(selectedEvent.type) }}>
              <span className="modal-type">{selectedEvent.type.toUpperCase()}</span>
            </div>
            <div className="modal-content">
              <h2>{selectedEvent.title}</h2>
              <p><strong>Date:</strong> {new Date(selectedEvent.date).toLocaleDateString()}</p>
              <p><strong>Time:</strong> {selectedEvent.time}</p>
              <p><strong>Location:</strong> {selectedEvent.location}</p>
              <p><strong>Team:</strong> {selectedEvent.team}</p>
              <p>{selectedEvent.description}</p>
            </div>
          </div>
        </div>
      )}

      <section className="schedule-cta">
        <h2>NEVER MISS AN EVENT</h2>
        <p>Register to receive notifications about upcoming matches and training.</p>
        <Link to="/register" className="btn-primary">REGISTER NOW</Link>
      </section>
    </div>
  );
}

export default Schedule;
